import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useResetPassword } from "./api";
import {
  ResetPasswordFormData,
  resetPasswordSchema,
} from "./schemas/resetPasswordSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import pwVisibleIcon from "../../assets/pw_visible.svg";
import zxcvbn from "zxcvbn";

export default function ResetPassword() {
  const resetPasswordMutation = useResetPassword();
  const navigate = useNavigate();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = (data: ResetPasswordFormData) => {
    if (!navigator.onLine) {
      toast.error("No internet connection. Please check your network.");
      return;
    }
    const result = zxcvbn(data.password, []);
    if (result.score < 3) {
      toast.error(
        result.feedback.warning || "Password is too weak or too common"
      );
      return;
    }
    resetPasswordMutation.mutate(
      { ...data },
      {
        onSuccess: () => {
          toast.success("Password reset successfully", {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: true,
            theme: "colored",
          });
          navigate("/profile");
        },
        onError: (err: any) => {
          toast.error(
            err.response?.data?.message ||
              "An error occurred while resetting your password"
          );
        },
      }
    );
  };

  return (
    <div className='flex justify-center mt-32'>
      <div className='flex flex-col border border-[#D9D9D9] w-3/4 rounded-md items-center p-6 space-y-4 sm:w-3/4 md:w-1/2 lg:w-1/3'>
        <h1 className='text-lg font-bold'>Enter Your New Password</h1>

        <form onSubmit={handleSubmit(onSubmit)} className='w-full space-y-4'>
          <div className='mb-16'>
            <div className='flex flex-col w-full relative mb-6'>
              <label htmlFor='password' className='mb-1'>
                Password
              </label>
              <input
                id='password'
                type={isPasswordVisible ? "text" : "password"}
                autoComplete='new-password'
                {...register("password")}
                className='border border-[#D9D9D9] rounded-md px-2 py-1 outline-none w-full'
                placeholder='Password'
              />
              <button
                type='button'
                tabIndex={0}
                aria-label={
                  isPasswordVisible ? "Hide password" : "Show password"
                }
                className='absolute right-2 top-8'
                onClick={() => setIsPasswordVisible((v) => !v)}
              >
                <img
                  src={pwVisibleIcon}
                  alt='toggle visibility'
                  className='w-7 h-7'
                />
              </button>
              {errors.password && (
                <p className='text-red-500 text-sm -mb-5'>
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className='flex flex-col w-full relative mb-6'>
              <label htmlFor='confirm-password' className='mb-1'>
                Confirm Password
              </label>
              <input
                id='confirm-password'
                type={isConfirmPasswordVisible ? "text" : "password"}
                autoComplete='new-password'
                {...register("confirmPassword")}
                className='border border-[#D9D9D9] rounded-md px-2 py-1 outline-none w-full'
                placeholder='Confirm Password'
              />
              <button
                type='button'
                tabIndex={0}
                aria-label={
                  isConfirmPasswordVisible ? "Hide password" : "Show password"
                }
                className='absolute right-2 top-8'
                onClick={() => setIsConfirmPasswordVisible((v) => !v)}
              >
                <img
                  src={pwVisibleIcon}
                  alt='toggle visibility'
                  className='w-7 h-7'
                />
              </button>
              {errors.confirmPassword && (
                <p className='text-red-500 text-sm -mb-5'>
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
          </div>

          <div className='flex w-full justify-between space-x-2 mt'>
            <Link to='/profile' className='w-1/2'>
              <button className='bg-[#E3E3E3] text-black rounded-md w-full py-2 hover:bg-[#D9D9D9]'>
                Cancel
              </button>
            </Link>
            <button
              disabled={resetPasswordMutation.isPending}
              className='bg-[#FF5F02] text-white rounded-md w-1/2 py-2 hover:bg-[#FF4C00]'
            >
              {resetPasswordMutation.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
